import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Search, Building2, Plus, MapPin, FileText } from 'lucide-react';

export const PartySelector = ({ open, onOpenChange, parties, onSelect, onCreateNew }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredParties = parties.filter(party => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      party.name?.toLowerCase().includes(query) ||
      party.gst_number?.toLowerCase().includes(query) ||
      party.address?.toLowerCase().includes(query)
    );
  });

  const handleSelect = (party) => {
    setSearchQuery('');
    onSelect(party);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[90vh] p-0" data-testid="party-selector-modal">
        <SheetHeader className="p-4 border-b sticky top-0 bg-white z-10">
          <SheetTitle className="text-lg font-semibold">Select Party</SheetTitle>
          {/* Search */}
          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search by name, GST, or address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12"
              data-testid="party-selector-search"
              autoFocus
            />
          </div>
        </SheetHeader>
        
        <ScrollArea className="h-[calc(90vh-140px)]">
          <div className="p-4 space-y-2">
            {/* Quick Create Button */}
            <Button
              variant="outline"
              className="w-full h-14 border-dashed justify-start gap-3"
              onClick={() => {
                onOpenChange(false);
                onCreateNew();
              }}
              data-testid="quick-create-party-btn"
            >
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Plus className="w-5 h-5 text-amber-600" />
              </div>
              <span className="font-medium">Create New Party</span>
            </Button>

            {filteredParties.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">
                  {searchQuery ? 'No parties found matching your search' : 'No parties available'}
                </p>
              </div>
            ) : (
              filteredParties.map(party => (
                <button
                  key={party.id}
                  className="selector-item w-full text-left rounded-lg hover:bg-slate-50 transition-colors"
                  onClick={() => handleSelect(party)}
                  data-testid={`party-option-${party.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-5 h-5 text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-slate-900 truncate">{party.name}</h3>
                      {party.address && (
                        <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5 truncate">
                          <MapPin size={12} className="flex-shrink-0" />
                          {party.address}
                        </p>
                      )}
                      {party.gst_number && (
                        <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                          <FileText size={12} className="flex-shrink-0" />
                          GST: {party.gst_number}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default PartySelector;
